import { Controller } from "@hotwired/stimulus"

declare global {
  interface Window {
    adsenseBlocked?: boolean
  }
}

// Stamp deadline, measured from the unit first entering the viewport — Google
// defers below-fold ad requests until the slot nears the screen, so a page-load
// timer would swap slots Google never had a chance to fill. A visible ins still
// bare after this long means the ads script was neutered by a blocker.
const AD_STATUS_DEADLINE_MS = 10000

// Swaps the AdSense unit for a random house ad cloned from the server-rendered
// template pool when the unit goes unfilled or an ad blocker is detected.
export default class extends Controller<HTMLElement> {
  static targets = ["unit", "pool"]

  declare readonly unitTarget: HTMLElement
  declare readonly hasUnitTarget: boolean
  declare readonly poolTarget: HTMLTemplateElement

  observer?: MutationObserver
  visibilityObserver?: IntersectionObserver
  deadline?: number
  swapped = false

  connect() {
    // After a swap the unit is gone; on a Turbo restore of that state there is
    // nothing left to observe and the house ad simply stays.
    if (!this.hasUnitTarget) return

    this.observer = new MutationObserver(() => this.swapIfUnfilled())
    this.observer.observe(this.unitTarget, {
      attributes: true,
      attributeFilter: ["data-ad-status"],
    })
    this.swapIfUnfilled()

    if (this.unitTarget.tagName !== "INS") return

    // The adsense:blocked event (script onerror) arrives via a Stimulus action
    // on the wrapper; the flag covers units connecting after it already fired.
    if (window.adsenseBlocked) {
      this.swapToHouseAd()
      return
    }
    this.visibilityObserver = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return

      this.visibilityObserver?.disconnect()
      this.deadline = window.setTimeout(() => {
        if (this.hasUnitTarget && !this.unitTarget.dataset.adStatus) this.swapToHouseAd()
      }, AD_STATUS_DEADLINE_MS)
    })
    this.visibilityObserver.observe(this.unitTarget)
  }

  disconnect() {
    this.teardown()
  }

  // Fires when a blocker prunes the ins from the DOM (and on our own swap or a
  // Turbo teardown, which both must not re-trigger a swap).
  unitTargetDisconnected() {
    if (this.swapped || !this.element.isConnected) return

    this.swapToHouseAd()
  }

  swapIfUnfilled() {
    if (this.unitTarget.dataset.adStatus !== "unfilled") return

    this.swapToHouseAd()
  }

  swapToHouseAd() {
    if (this.swapped) return

    this.swapped = true
    this.teardown()
    const ads = this.poolTarget.content.children
    const ad = ads[Math.floor(Math.random() * ads.length)]
    if (!ad) return

    const clone = ad.cloneNode(true)
    if (this.hasUnitTarget) {
      this.unitTarget.replaceWith(clone)
    } else {
      this.element.prepend(clone)
    }
  }

  teardown() {
    this.observer?.disconnect()
    this.visibilityObserver?.disconnect()
    window.clearTimeout(this.deadline)
  }
}
